/**
 * Side icons of the menu sit in two independent columns: events on the left,
 * offers on the right by default. Each column is packed from the top in its
 * own `order`, so there are never gaps between visible icons, and icons past
 * the last row of a column are hidden. Which side an icon goes to, its order
 * and whether it shows at all come from the server content-config
 * `menu_layout`; whether it is active (unlock level, placement, timers) is
 * still decided by the icon's own config.
 */

export type MenuSideSlotId =
  | "win_streak_tower"
  | "tournament"
  | "daily_challenge"
  | "daily_tasks"
  | "key_hunt"
  | "prize_shot"
  | "no_ads"
  | "mystery_box"
  | "one_plus_one"
  | "one_plus_two"
  | "chain_offer"
  | "chain_video";

export type MenuSideColumn = "left" | "right";

export interface MenuLayoutItem
{
  side: MenuSideColumn;
  /** Lower comes first within the column. */
  order: number;
  enabled: boolean;
}

export interface MenuLayoutConfig
{
  /** Slots per column. */
  rows: number;
  items: Record<MenuSideSlotId, MenuLayoutItem>;
}

export const MENU_SIDE_SLOT_IDS: readonly MenuSideSlotId[] = [
  "win_streak_tower", 
  "tournament", 
  "daily_challenge", 
  "daily_tasks", 
  "key_hunt",
  "prize_shot",
  "no_ads", 
  "mystery_box", 
  "one_plus_one", 
  "one_plus_two", 
  "chain_offer", 
  "chain_video",
];

/** Gaps of 10 leave room to slot a new icon between two others. */
export const DEFAULT_MENU_LAYOUT: MenuLayoutConfig = {
  rows: 6,
  items: {
    prize_shot: { side: "left", order: 5, enabled: true },
    win_streak_tower: { side: "left", order: 10, enabled: true },
    tournament: { side: "left", order: 20, enabled: true },
    daily_challenge: { side: "left", order: 30, enabled: true },
    daily_tasks: { side: "left", order: 40, enabled: true },
    key_hunt: { side: "left", order: 50, enabled: true },
    no_ads: { side: "right", order: 10, enabled: true },
    mystery_box: { side: "right", order: 20, enabled: true },
    one_plus_one: { side: "right", order: 30, enabled: true },
    one_plus_two: { side: "right", order: 40, enabled: true },
    chain_offer: { side: "right", order: 50, enabled: true },
    chain_video: { side: "right", order: 60, enabled: true },
  },
};

/** Rows fit between the top bar and the play button; more would overlap them. */
const MAX_ROWS = 6;

function asRecord( value: unknown ): Record<string, unknown>
{
  return value && typeof value === "object" && !Array.isArray( value )
    ? value as Record<string, unknown>
    : {};
}

/**
 * Server payload → layout. Unknown ids are dropped, missing ids and mistyped
 * fields fall back to the bundled default field by field, so a server that
 * does not know a newer icon still gets it placed.
 */
export function normalizeMenuLayout( raw: unknown ): MenuLayoutConfig
{
  const value = asRecord( raw );
  const rows_raw = Number( value.rows );
  const rows = Number.isFinite( rows_raw ) && rows_raw >= 1
    ? Math.min( MAX_ROWS, Math.floor( rows_raw ) )
    : DEFAULT_MENU_LAYOUT.rows;

  const raw_items = asRecord( value.items );
  const items = {} as Record<MenuSideSlotId, MenuLayoutItem>;
  for ( const id of MENU_SIDE_SLOT_IDS )
  {
    const item = asRecord( raw_items[id] );
    const fallback = DEFAULT_MENU_LAYOUT.items[id];
    const order = Number( item.order );
    items[id] = {
      side: item.side === "left" || item.side === "right" ? item.side : fallback.side,
      order: item.order !== null && item.order !== "" && Number.isFinite( order ) ? order : fallback.order,
      enabled: typeof item.enabled === "boolean" ? item.enabled : fallback.enabled,
    };
  }
  return { rows, items };
}

export interface MenuSideSlot
{
  id: MenuSideSlotId;
  column: MenuSideColumn;
  /** 0-based row within the column. */
  row: number;
}

/**
 * Slots for the visible icons. Disabled icons and icons past the last row of
 * their column get none (the caller hides them); ties on order fall back to
 * the id so the result never depends on the order of the input.
 */
export function assignMenuSideSlots( visible: readonly MenuSideSlotId[], layout: MenuLayoutConfig = DEFAULT_MENU_LAYOUT ): MenuSideSlot[]
{
  const ids = [ ...new Set( visible ) ].filter( id => layout.items[id]?.enabled );
  const slots: MenuSideSlot[] = [];
  for ( const column of [ "left", "right" ] as const )
  {
    ids.filter( id => layout.items[id].side === column )
      .sort( ( a, b ) => layout.items[a].order - layout.items[b].order || a.localeCompare( b ) )
      .slice( 0, layout.rows )
      .forEach( ( id, row ) => slots.push( { id, column, row } ) );
  }
  return slots;
}
